/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect, useState } from 'react';

export function debounce<T extends (...args: unknown[]) => unknown>(fn: T, ms?: number): T {
  let timer: number | undefined;

  return ((_) => {
    clearTimeout(timer);
    timer = setTimeout((_) => {
      timer = undefined;
      // @ts-expect-error unspecified type
      // eslint-disable-next-line prefer-rest-params
      fn.apply(this, arguments);
    }, ms);
  }) as T;
}

export default function useDebounce(value: string, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}
